import { rd, RemoteData } from '@passionware/monads';
import { isEqual } from 'lodash';
import { ReactNode, Ref } from 'react';
import {
  Combobox,
  ComboboxButton,
  ComboboxButtonProps,
  ComboboxContent,
  ComboboxCreateItemQuery,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxQueryRenderer,
  ComboboxTrigger
} from 'v5/design-system/Combobox';
import { InputGroupButton } from 'v5/design-system/InputGroup';
import { Skeleton } from 'v5/design-system/Skeleton';
import { renderError } from 'v5/patterns/widgets/renderError';
import { useFieldContext } from '../FieldContext';
import { CreateFromQueryItem } from './CreateFromQueryItem';

/**
 * Data hooks for one selector. Both are React hooks: they are called on every
 * render of the selector, always in the same order.
 */
export type AsyncSelectorSource<FormValue, OptionValue> = {
  /** Label of the current value (idle when `null`). */
  useOption: (value: FormValue | null) => RemoteData<ReactNode>;
  useOptions: (
    query: string
  ) => RemoteData<Array<{ value: OptionValue; label: ReactNode }>>;
  optionValueToFormValue: (value: OptionValue) => FormValue;
  optionValueToKey: (value: OptionValue) => string;
};

/**
 * Where the trigger lives: on its own (bordered per context variant) or as a
 * headless button inside an `InputGroup` row.
 */
export type AsyncSelectorAppearance =
  | { type: 'standalone' }
  | { type: 'input-group' };

export type AsyncSelectorProps<FormValue, OptionValue> = {
  source: AsyncSelectorSource<FormValue, OptionValue>;
  value: FormValue | null;
  onChange: (value: FormValue | null) => void;
  onBlur: () => void;
  name: string;
  triggerRef: Ref<HTMLButtonElement>;
  placeholder: string;
  searchPrompt: string;
  emptyContent: ReactNode;
  appearance: AsyncSelectorAppearance;
  autoFocus: boolean;
  /**
   * Creates an option from the search text and selects it; `null` when the
   * user may not create one. Resolves `null` when the creation failed.
   */
  create: ((query: string) => Promise<FormValue | null>) | null;
  /** Content of the create option; `ComboboxCreateItemQuery` shows the search text. */
  createLabel?: ReactNode;
};

function OptionDisplay<FormValue>({
  value,
  placeholder,
  useOption
}: {
  value: FormValue | null;
  placeholder: string;
  useOption: (value: FormValue | null) => RemoteData<ReactNode>;
}) {
  const option = rd.useLastWithPlaceholder(useOption(value));
  if (value === null) {
    return <>{placeholder}</>;
  }
  return rd
    .fullJourney(option)
    .initially(placeholder)
    .wait(<Skeleton className="w-full h-4" />)
    .catch(renderError)
    .map(label => label ?? placeholder);
}

/**
 * Single-value async selector (without a form binding): a combobox whose options come from a hook keyed by the
 * search query. Selecting the current option again clears the value.
 */
export function AsyncSelector<FormValue, OptionValue>({
  source,
  value,
  onChange,
  onBlur,
  name,
  triggerRef,
  placeholder,
  searchPrompt,
  emptyContent,
  appearance,
  autoFocus,
  create,
  createLabel = (
    <>
      Create new: <ComboboxCreateItemQuery />
    </>
  )
}: AsyncSelectorProps<FormValue, OptionValue>) {
  const ctx = useFieldContext();
  const size: ComboboxButtonProps['size'] = ctx.size === 'lg' ? 'md' : ctx.size;
  const readOnly = ctx.mode === 'display';

  const button = (
    <ComboboxButton
      size={size}
      variant={appearance.type === 'input-group' ? 'headless' : ctx.variant}
      className={appearance.type === 'input-group' ? 'w-full' : undefined}
      readOnly={readOnly}
      ref={triggerRef}
      name={name}
      onBlur={onBlur}
      autoFocus={autoFocus}
    >
      <OptionDisplay
        value={value}
        placeholder={placeholder}
        useOption={source.useOption}
      />
    </ComboboxButton>
  );

  return (
    <Combobox>
      <ComboboxTrigger asChild>
        {appearance.type === 'input-group' ? (
          <InputGroupButton asChild className="flex-1 justify-start">
            {button}
          </InputGroupButton>
        ) : (
          button
        )}
      </ComboboxTrigger>
      <ComboboxContent>
        <ComboboxInput placeholder={searchPrompt} />
        <ComboboxList>
          <ComboboxQueryRenderer emptyContent={emptyContent}>
            {query =>
              rd.useLastWithPlaceholder(
                rd.map(source.useOptions(query), items => (
                  <ComboboxGroup>
                    {items.map(item => {
                      const formValue = source.optionValueToFormValue(
                        item.value
                      );
                      const selected = isEqual(value, formValue);
                      const key = source.optionValueToKey(item.value);
                      return (
                        <ComboboxItem
                          key={key}
                          value={key}
                          onSelect={() => onChange(selected ? null : formValue)}
                          isSelected={selected}
                        >
                          {item.label}
                        </ComboboxItem>
                      );
                    })}
                  </ComboboxGroup>
                ))
              )
            }
          </ComboboxQueryRenderer>
          {create && (
            <CreateFromQueryItem
              onSelect={query =>
                void create(query).then(created => {
                  if (created !== null) onChange(created);
                })
              }
            >
              {createLabel}
            </CreateFromQueryItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
